/*
 * XYNE Redesign — Header controller
 *
 * Handles:
 *  1. Sticky shrink-on-scroll behavior (body class + transition)
 *  2. Mobile drawer open/close (hamburger button)
 *  3. Account dropdown open/close (account icon button) — closes #95
 *  4. Cart count sync — listens for cart updates from RDCart
 */
(function () {
  'use strict';

  function init() {
    // ---------- 1. Sticky shrink-on-scroll ----------
    var SCROLL_THRESHOLD = 60;
    var scrollHandler = function () {
      if (window.scrollY > SCROLL_THRESHOLD) {
        document.body.classList.add('rd-header-scrolled');
      } else {
        document.body.classList.remove('rd-header-scrolled');
      }
    };
    scrollHandler();
    window.addEventListener('scroll', scrollHandler, { passive: true });

    // ---------- 2. Mobile drawer ----------
    var drawer = document.querySelector('[data-rd-mobile-drawer]');
    var drawerBackdrop = document.querySelector('[data-rd-drawer-backdrop]');
    var drawerOpenBtn = document.querySelector('[data-rd-drawer-open]');
    var drawerCloseBtns = Array.prototype.slice.call(document.querySelectorAll('[data-rd-drawer-close]'));
    var drawerLastFocus = null;

    function openDrawer() {
      if (!drawer) return;
      drawerLastFocus = document.activeElement;
      drawer.hidden = false;
      if (drawerBackdrop) drawerBackdrop.hidden = false;
      requestAnimationFrame(function () {
        document.body.classList.add('rd-drawer-open');
      });
      if (drawerOpenBtn) drawerOpenBtn.setAttribute('aria-expanded', 'true');
      // Focus the close button after slide-in
      setTimeout(function () {
        var closeBtn = drawer.querySelector('[data-rd-drawer-close]');
        if (closeBtn) closeBtn.focus();
      }, 320);
    }
    function closeDrawer() {
      document.body.classList.remove('rd-drawer-open');
      setTimeout(function () {
        if (!document.body.classList.contains('rd-drawer-open')) {
          if (drawer) drawer.hidden = true;
          if (drawerBackdrop) drawerBackdrop.hidden = true;
        }
      }, 320);
      if (drawerOpenBtn) drawerOpenBtn.setAttribute('aria-expanded', 'false');
      if (drawerLastFocus && typeof drawerLastFocus.focus === 'function') {
        drawerLastFocus.focus();
      }
    }

    if (drawerOpenBtn) drawerOpenBtn.addEventListener('click', openDrawer);
    drawerCloseBtns.forEach(function (btn) { btn.addEventListener('click', closeDrawer); });
    if (drawerBackdrop) drawerBackdrop.addEventListener('click', closeDrawer);

    // Close drawer when a nav link inside it is clicked (so the drawer doesn't
    // persist after navigation on SPA-y behaviors)
    if (drawer) {
      drawer.addEventListener('click', function (e) {
        var anchor = e.target.closest('a[href]');
        if (anchor && anchor.getAttribute('href').indexOf('#') !== 0) {
          // Real navigation — close after a brief delay so the click registers
          setTimeout(closeDrawer, 80);
        }
      });
    }

    // ---------- 3. Account dropdown ----------
    // The account toggle is now an <a href="/account"> rather than a <button>
    // so clicking falls back to native navigation if JS can't open the
    // dropdown (e.g. JS error, blocked extension, missing menu element).
    // We hijack the click here when both elements are present.
    var accountBtn = document.querySelector('[data-rd-account-toggle]');
    var accountMenu = document.querySelector('[data-rd-account-menu]');
    var accountLastFocus = null;

    // CRITICAL: reparent the menu to <body>. The header has
    // `backdrop-filter: blur(...)` which establishes a containing block for
    // position:fixed descendants — meaning the mobile bottom-sheet would
    // anchor to the bottom of the HEADER, not the viewport. Detaching the
    // menu fixes this on every viewport.
    if (accountMenu && accountMenu.parentElement !== document.body) {
      document.body.appendChild(accountMenu);
    }

    // Position the desktop dropdown by reading the account button's
    // bounding rect. Mobile (≤899px) uses a bottom-sheet style and
    // ignores these vars. Called on open and on resize/scroll while open.
    function positionAccountMenu() {
      if (!accountMenu || !accountBtn) return;
      if (window.innerWidth <= 899) return; // mobile bottom-sheet — skip
      var rect = accountBtn.getBoundingClientRect();
      accountMenu.style.setProperty('--rd-acct-top', (rect.bottom + 8) + 'px');
      accountMenu.style.setProperty('--rd-acct-right', Math.max(8, window.innerWidth - rect.right) + 'px');
    }

    function openAccount() {
      if (!accountMenu) return;
      accountLastFocus = document.activeElement;
      positionAccountMenu();
      document.body.classList.add('rd-account-menu-open');
      if (accountBtn) accountBtn.setAttribute('aria-expanded', 'true');
    }
    function closeAccount() {
      document.body.classList.remove('rd-account-menu-open');
      if (accountBtn) accountBtn.setAttribute('aria-expanded', 'false');
    }
    function toggleAccount() {
      if (document.body.classList.contains('rd-account-menu-open')) closeAccount();
      else openAccount();
    }

    // Keep dropdown anchored to the account button if the user resizes
    // the window or scrolls (sticky shrink-on-scroll changes header height).
    window.addEventListener('resize', function () {
      if (document.body.classList.contains('rd-account-menu-open')) positionAccountMenu();
    });
    window.addEventListener('scroll', function () {
      if (document.body.classList.contains('rd-account-menu-open')) positionAccountMenu();
    }, { passive: true });

    if (accountBtn && accountMenu) {
      accountBtn.addEventListener('click', function (e) {
        // Hijack the link click — show dropdown instead of navigating.
        // If something below this throws, the native navigation still
        // happens because preventDefault is the FIRST thing we do.
        e.preventDefault();
        e.stopPropagation();
        toggleAccount();
      });

      // Close on outside click
      document.addEventListener('click', function (e) {
        if (!document.body.classList.contains('rd-account-menu-open')) return;
        if (accountMenu.contains(e.target) || accountBtn.contains(e.target)) return;
        closeAccount();
      });

      // Close on Esc
      document.addEventListener('keydown', function (e) {
        if (e.key !== 'Escape') return;
        if (document.body.classList.contains('rd-account-menu-open')) {
          closeAccount();
          if (accountLastFocus && typeof accountLastFocus.focus === 'function') {
            accountLastFocus.focus();
          }
        }
      });

      // Close when any link in the menu is clicked (then navigation happens)
      accountMenu.addEventListener('click', function (e) {
        if (e.target.closest('a')) {
          setTimeout(closeAccount, 80);
        }
      });
    }

    // ---------- 4. Cart icon → open Atlas Bolt's <cart-drawer> ----------
    // The redesigned cart drawer is disabled — Brandon prefers Atlas Bolt's
    // native side drawer. The header cart icon and the mobile drawer cart
    // link both carry [data-rd-cart-open]. Clicking either opens Atlas Bolt's
    // <cart-drawer> if it's mounted, otherwise we fall through to the link's
    // /cart navigation (e.g. on the cart page where the drawer is suppressed).
    document.addEventListener('click', function (e) {
      var trigger = e.target.closest('[data-rd-cart-open]');
      if (!trigger) return;

      var drawer = document.querySelector('cart-drawer');
      if (!drawer || typeof drawer.open !== 'function') return; // native nav

      e.preventDefault();
      drawer.open(trigger);

      // If we're inside the mobile drawer, close it so the cart drawer takes
      // over the screen instead of stacking modals.
      if (document.body.classList.contains('rd-drawer-open')) {
        document.body.classList.remove('rd-drawer-open');
        setTimeout(function () {
          var mobileDrawer = document.querySelector('[data-rd-mobile-drawer]');
          var mobileBackdrop = document.querySelector('[data-rd-drawer-backdrop]');
          if (mobileDrawer) mobileDrawer.hidden = true;
          if (mobileBackdrop) mobileBackdrop.hidden = true;
        }, 320);
      }
    });

    // ---------- 5. Cart count sync ----------
    // The redesign header has its own cart count bubble (`.rd-header__cart-count`)
    // which is independent of Atlas Bolt's hidden `#cart-icon-bubble`. Atlas Bolt's
    // cart-drawer.js + cart.js update `#cart-icon-bubble` on every cart change
    // (add-to-cart, qty change, remove) — so we mirror that into our visible
    // redesign bubble. Without this bridge, the visible cart count only updates
    // on full page refresh.
    //
    // We also subscribe to PUB_SUB_EVENTS.cartUpdate as a belt-and-suspenders
    // fallback, fetching /cart.js to get the authoritative count.
    var cartCountEl = document.querySelector('.rd-header__cart-count');
    var mobileCartCountEl = document.querySelector('[data-rd-mobile-cart-count]');

    function setCartCount(n) {
      n = parseInt(n, 10) || 0;
      if (cartCountEl) {
        cartCountEl.textContent = String(n);
        cartCountEl.classList.toggle('is-empty', n === 0);
      }
      if (mobileCartCountEl) {
        mobileCartCountEl.textContent = n + ' ' + (n === 1 ? 'item' : 'items');
      }
      // Update aria-label on the cart link so screen readers stay in sync
      var cartLinks = document.querySelectorAll('[data-rd-cart-open][aria-label]');
      cartLinks.forEach(function (link) {
        link.setAttribute('aria-label', 'Cart, ' + n + ' ' + (n === 1 ? 'item' : 'items'));
      });
    }

    // 5a. Mirror Atlas Bolt's hidden #cart-icon-bubble whenever it changes.
    // Atlas Bolt re-renders the bubble via getSectionInnerHTML which sets
    // innerHTML, so a MutationObserver with subtree:true catches every update.
    var atlasBubble = document.getElementById('cart-icon-bubble');
    if (atlasBubble) {
      var atlasObserver = new MutationObserver(function () {
        var visibleCount = atlasBubble.querySelector('.cart-count-bubble [aria-hidden="true"]');
        if (visibleCount) {
          setCartCount(visibleCount.textContent.trim());
        } else {
          // Atlas Bolt removes the .cart-count-bubble entirely when cart is empty
          setCartCount(0);
        }
      });
      atlasObserver.observe(atlasBubble, {
        childList: true,
        subtree: true,
        characterData: true
      });
    }

    // 5b. Fallback: subscribe to the cartUpdate event Atlas Bolt publishes from
    // product-form.js + cart.js. We fetch /cart.js so we don't depend on DOM
    // timing; this catches edge cases where the mutation observer misses.
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
      subscribe(PUB_SUB_EVENTS.cartUpdate, function () {
        fetch(window.Shopify && window.Shopify.routes && window.Shopify.routes.root
          ? window.Shopify.routes.root + 'cart.js'
          : '/cart.js')
          .then(function (r) { return r.json(); })
          .then(function (cart) { if (cart && typeof cart.item_count === 'number') setCartCount(cart.item_count); })
          .catch(function () { /* swallow — observer above is the primary path */ });
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
